import React from "react";
import { useTranslation } from "react-i18next";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { ConditionCard } from "@/components/cards/ConditionCard";
import { CONDITIONS, CONDITION_CATEGORIES } from "@/data/conditions";
import { useLocalize } from "@/data/localize";
import { SEO } from "@/lib/seo";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

// FR labels for the fixed category set. Falls back to EN if unknown.
const CATEGORY_LABELS_FR = {
    All: "Toutes",
    "Upper GI": "GI supérieur",
    "Lower GI": "GI inférieur",
    "Inflammatory Bowel Disease": "Maladie inflammatoire de l'intestin",
    "Functional GI": "GI fonctionnel",
    Liver: "Foie",
    "Small Bowel": "Intestin grêle",
    "General GI": "GI général",
};

export default function ConditionsPage() {
    const { t, i18n } = useTranslation();
    const { localizeList } = useLocalize();
    const isFr = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase().startsWith("fr");
    const conditions = localizeList(CONDITIONS);

    const [query, setQuery] = React.useState("");
    const [category, setCategory] = React.useState("All");

    const catLabel = (c) => (isFr && CATEGORY_LABELS_FR[c] ? CATEGORY_LABELS_FR[c] : c);

    const filtered = conditions.filter((c) => {
        // c.category may already be localized — use the raw EN CONDITIONS entry
        // by slug to filter deterministically
        const raw = CONDITIONS.find((x) => x.slug === c.slug);
        const rawCat = raw ? raw.category : c.category;
        const matchesCat = category === "All" || rawCat === category;
        const q = query.trim().toLowerCase();
        const matchesQ =
            !q ||
            (c.name || "").toLowerCase().includes(q) ||
            (c.summary || "").toLowerCase().includes(q) ||
            (c.category || "").toLowerCase().includes(q);
        return matchesCat && matchesQ;
    });

    return (
        <>
            <SEO title={t("conditionsPage.seoTitle")} description={t("conditionsPage.seoDescription")} />
            <div className="container-page pt-8">
                <Breadcrumbs items={[{ label: t("conditionsPage.chip") }]} />
            </div>
            <section className="container-page pt-6 pb-4">
                <div className="max-w-3xl">
                    <span className="chip-teal">{t("conditionsPage.chip")}</span>
                    <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold text-[color:var(--brand-navy)] leading-tight">
                        {t("conditionsPage.title")}
                    </h1>
                    <p className="mt-4 text-[color:var(--text-muted)] leading-relaxed text-lg">
                        {t("conditionsPage.intro")}
                    </p>
                </div>
            </section>

            <section className="container-page pb-6">
                <div className="card-soft p-4 sm:p-5 flex flex-col md:flex-row gap-3 md:items-center">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--text-soft)]" />
                        <Input
                            data-testid="conditions-search-input"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={t("conditionsPage.searchPlaceholder")}
                            className="pl-9 h-11 rounded-xl"
                        />
                    </div>
                    <div className="flex flex-wrap gap-2" data-testid="conditions-filter-tabs">
                        {CONDITION_CATEGORIES.map((c) => (
                            <button
                                key={c}
                                onClick={() => setCategory(c)}
                                data-testid={`conditions-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                                    category === c
                                        ? "bg-[color:var(--brand-navy)] text-white"
                                        : "bg-[color:var(--bg-subtle)] text-[color:var(--text-muted)] hover:bg-[color:var(--brand-teal-soft)] hover:text-[color:var(--brand-teal-2)]"
                                }`}
                            >
                                {catLabel(c)}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="container-page pb-6">
                {filtered.length === 0 ? (
                    <div
                        className="card-soft p-10 text-center"
                        data-testid="conditions-empty-state"
                    >
                        <p className="text-[color:var(--text-muted)]">
                            {t("conditionsPage.emptyState")}
                        </p>
                    </div>
                ) : (
                    <div
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
                        data-testid="conditions-results-grid"
                    >
                        {filtered.map((c) => (
                            <ConditionCard key={c.slug} condition={c} />
                        ))}
                    </div>
                )}
            </section>
        </>
    );
}
